// RainModeOverlay
// ===============
//
// Wave 14ca-fix64: Vollflaechiger Lock-Overlay fuer den Rain-Mode.
//
// Verhalten:
//   - Active: Pointer-Events:auto, schluckt alle touch/mouse/pointer events
//     auf der ganzen Viewport. Karte/UI darunter sind sichtbar (semi-
//     transparenter Hintergrund), aber nicht antippbar.
//   - Recording-Stats sind weiter sichtbar (kommen vom darunter liegenden
//     ActiveRideOverlay — das hier liegt nur darueber als Click-Schlucker).
//   - Entsperrungs-Geste: 3 gleichzeitige Touches fuer 2 s. Visualisiert
//     mit einem ringfoermigen Progress-Indikator in der Mitte; sobald 3
//     Touches da sind faengt der Ring an zu fuellen, sobald er voll ist
//     wird der Mode deaktiviert.
//
// Robustheit:
//   - touchstart/move/end statt pointer-events: TouchEvent gibt uns
//     `event.touches.length` direkt; PointerEvent waere multi-touch-
//     awareness aufwaendig (eigene Map<pointerId,...>).
//   - touchcancel zaehlt wie touchend (z.B. iOS palm-rejection).
//   - Wenn waehrend des Hold ein Finger gehoben wird → Reset auf 0%
//     statt eingefroren — sonst koennten 3 schnell hintereinander
//     getapte Finger akkumulieren.

import { useEffect, useRef, useState } from 'react';
import { rainModeBus } from '../lib/rainModeBus';

const UNLOCK_HOLD_MS = 2000;
const UNLOCK_TOUCH_COUNT = 3;

export function RainModeOverlay() {
  const [active, setActive] = useState(() => rainModeBus.get());
  useEffect(() => {
    setActive(rainModeBus.get());
    return rainModeBus.subscribe(setActive);
  }, []);

  // 0..1 — Progress-Wert fuer den Ring. Ref damit RAF-Loop ohne re-renders
  // den Wert hochziehen kann; State spiegelt nur den aktuell gerenderten
  // Wert (rate-limited durch RAF).
  const [progress, setProgress] = useState(0);
  const holdStartRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) {
      // Cleanup beim Verlassen des Lock-Mode.
      holdStartRef.current = null;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setProgress(0);
      return;
    }

    const overlay = overlayRef.current;
    if (!overlay) return;

    const tick = () => {
      const start = holdStartRef.current;
      if (start == null) {
        setProgress(0);
        rafRef.current = null;
        return;
      }
      const elapsed = Date.now() - start;
      const p = Math.min(1, elapsed / UNLOCK_HOLD_MS);
      setProgress(p);
      if (p >= 1) {
        // Entsperren! State zuruecksetzen, dann Bus deaktivieren.
        holdStartRef.current = null;
        setProgress(0);
        rainModeBus.deactivate();
        rafRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const onTouchStart = (e: TouchEvent) => {
      // 3 oder mehr Touches → Hold starten (wenn noch nicht laufend).
      if (e.touches.length >= UNLOCK_TOUCH_COUNT) {
        if (holdStartRef.current == null) {
          holdStartRef.current = Date.now();
          if (rafRef.current == null) {
            rafRef.current = requestAnimationFrame(tick);
          }
        }
      }
      e.preventDefault();
      e.stopPropagation();
    };
    const onTouchEnd = (e: TouchEvent) => {
      // Ein Finger weniger → wenn unter Schwelle, Hold abbrechen.
      if (e.touches.length < UNLOCK_TOUCH_COUNT) {
        holdStartRef.current = null;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    const onTouchMove = (e: TouchEvent) => {
      // Verhindern dass die darunterliegende Karte gepant wird.
      e.preventDefault();
      e.stopPropagation();
    };
    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    // passive:false ist noetig damit preventDefault auf touchmove wirkt
    // (iOS Safari schreit sonst im Console-Warning).
    overlay.addEventListener('touchstart', onTouchStart, { passive: false });
    overlay.addEventListener('touchend', onTouchEnd, { passive: false });
    overlay.addEventListener('touchcancel', onTouchEnd, { passive: false });
    overlay.addEventListener('touchmove', onTouchMove, { passive: false });
    overlay.addEventListener('click', onClick, { capture: true });
    return () => {
      overlay.removeEventListener('touchstart', onTouchStart);
      overlay.removeEventListener('touchend', onTouchEnd);
      overlay.removeEventListener('touchcancel', onTouchEnd);
      overlay.removeEventListener('touchmove', onTouchMove);
      overlay.removeEventListener('click', onClick, { capture: true });
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      holdStartRef.current = null;
    };
  }, [active]);

  if (!active) return null;

  // Wave 14ca-fix64-v2: Redesign nach User-Feedback "alles hinter Glas,
  // 3 Punkte + Ring + Text in der Mitte stoeren ungemein".
  //
  // Aenderungen:
  //   - Hintergrund komplett transparent (KEIN dunkler Tint mehr) — Karten-
  //     Farben sehen genauso aus wie ohne Rain-Mode.
  //   - Cyan-Border bleibt als einziger visueller Hinweis "Lock aktiv".
  //   - Mittiger Ring + 3 Punkte + Text → komplett entfernt.
  //   - Statt dessen: kleine kompakte Pill am UNTEREN Rand (knapp ueber der
  //     Map-Bottom-Toolbar mit Scale/Filter/POIs/altitude). Englisch:
  //     "Hold 3 fingers for 2s to unlock", konsistent mit dem Rest der App.
  //   - Beim Hold faellt eine duenne Progress-Bar von links nach rechts
  //     UNTEN in die Pill hinein — visueller Confirmation, ohne in der
  //     Map-Mitte rumzustehen.
  return (
    <div
      ref={overlayRef}
      // touch-action:none unterdrueckt Browser-Default-Gesten (pinch-zoom,
      // pull-to-refresh) waehrend der Mode aktiv ist.
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'transparent',
        touchAction: 'none',
        // Cyan-Border als einziger Mode-Indikator — schmal & dezent.
        boxShadow: 'inset 0 0 0 3px rgba(56, 189, 248, 0.85)',
      }}
      role="alertdialog"
      aria-label="Rain mode active — display locked"
    >
      {/* Wave 14ca-fix64-v3: Pill sitzt jetzt unmittelbar UEBER der
          Map-Bottom-Toolbar (Scale/Filter/POIs/altitude). Vorher 7rem
          → ueberlappte den Ride-FAB; jetzt 4.25rem (BottomBar selbst ist
          bei 1.5rem mit ~40px Hoehe + safe-area, daher ergibt 4.25rem
          eine klare Naehe zur Toolbar). Plus: Cyan-tinted Background
          statt dunkel — matcht den Rahmen, sticht hervor. */}
      <div
        style={{
          position: 'absolute',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.25rem)',
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          maxWidth: '92vw',
        }}
      >
        <div
          style={{
            position: 'relative',
            // Cyan-tinted statt dunkel — passend zum Rahmen.
            background: 'rgba(8, 145, 178, 0.85)', // ~ cyan-700 / sky-700 mit alpha
            border: '1px solid rgba(125, 211, 252, 0.7)', // sky-300
            borderRadius: 12,
            padding: '8px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#ffffff',
            fontSize: 13,
            fontWeight: 600,
            backdropFilter: 'blur(6px)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <span aria-hidden="true">🌧️</span>
          <span>Rain mode — hold 3 fingers for 2s to unlock</span>
          {/* Duenne Progress-Bar am unteren Rand der Pill — faellt ein
              waehrend die 3 Finger gehalten werden. Volle Breite = entsperrt.
              Auf cyan-Background machen wir sie WEISS damit der Kontrast
              klar bleibt. */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 3,
              background: 'rgba(255, 255, 255, 0.2)',
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          >
            <div
              style={{
                width: `${Math.round(progress * 100)}%`,
                height: '100%',
                background: '#ffffff',
                transition: 'width 80ms linear',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
